import {
  purchaseOrdersResponseSchema,
  discrepanciesResponseSchema,
  messagesResponseSchema,
  type PurchaseOrdersResponse,
  type DiscrepanciesResponse,
  type MessagesResponse,
  type POStatus,
  type SupplierPOAction,
  type ResolutionType,
} from '../types/orders';
import {
  FetchError, UpdateError, CreateError 
} from '../errors';
import {
  authFetch, buildApiUrl 
} from '../lib/api-client';

export interface POFilters {
  status?: POStatus;
  supplierId?: string;
}

export async function getSupplierPurchaseOrders(
  supplierId: string,
  filters?: Omit<POFilters, 'supplierId'>
): Promise<PurchaseOrdersResponse> {
  const params = new URLSearchParams();
  params.set('supplierId', supplierId);
  if (filters?.status) params.set('status', filters.status);

  const url = buildApiUrl('/v1/purchase-orders', params);
  const response = await authFetch(url);

  if (!response.ok) {
    throw new FetchError('purchase orders', response.statusText);
  }

  return purchaseOrdersResponseSchema.parse(await response.json());
}

export async function acknowledgePurchaseOrder(poId: string): Promise<void> {
  const url = buildApiUrl(`/v1/purchase-orders/${poId}/acknowledge`);
  const response = await authFetch(url, { method: 'POST' });

  if (!response.ok) {
    throw new UpdateError('PO acknowledgement', response.statusText);
  }
}

export async function updatePOStatus(poId: string, action: SupplierPOAction): Promise<void> {
  const url = buildApiUrl(`/v1/purchase-orders/${poId}/${action}`);
  const response = await authFetch(url, { method: 'POST' });

  if (!response.ok) {
    throw new UpdateError('PO', response.statusText);
  }
}

// Discrepancies / Pending Actions
export async function getDiscrepanciesByPO(poId: string): Promise<DiscrepanciesResponse> {
  const url = buildApiUrl(`/v1/purchase-orders/${poId}/discrepancies`);
  const response = await authFetch(url);

  if (!response.ok) {
    throw new FetchError('discrepancies', response.statusText);
  }

  return discrepanciesResponseSchema.parse(await response.json());
}

export async function getSupplierDiscrepancies(supplierId: string): Promise<DiscrepanciesResponse> {
  const url = buildApiUrl(`/v1/suppliers/${supplierId}/discrepancies`);
  const response = await authFetch(url);

  if (!response.ok) {
    throw new FetchError('discrepancies', response.statusText);
  }

  return discrepanciesResponseSchema.parse(await response.json());
}

export interface ProposeResolutionPayload {
  resolution: ResolutionType;
  creditNoteAmount?: number;
  message?: string;
}

export async function proposeResolution(
  discrepancyId: string,
  payload: ProposeResolutionPayload
): Promise<void> {
  const url = buildApiUrl(`/v1/discrepancies/${discrepancyId}/propose-resolution`);
  const response = await authFetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new UpdateError('resolution proposal', response.statusText);
  }
}

export async function acceptResolution(discrepancyId: string): Promise<void> {
  const url = buildApiUrl(`/v1/discrepancies/${discrepancyId}/accept`);
  const response = await authFetch(url, { method: 'POST' });

  if (!response.ok) {
    throw new UpdateError('resolution acceptance', response.statusText);
  }
}

export async function disputeResolution(
  discrepancyId: string,
  reason: string
): Promise<void> {
  const url = buildApiUrl(`/v1/discrepancies/${discrepancyId}/dispute`);
  const response = await authFetch(url, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    throw new UpdateError('dispute', response.statusText);
  }
}

// Messages / Communication
export async function getDiscrepancyMessages(discrepancyId: string): Promise<MessagesResponse> {
  const url = buildApiUrl(`/v1/discrepancies/${discrepancyId}/messages`);
  const response = await authFetch(url);

  if (!response.ok) {
    throw new FetchError('messages', response.statusText);
  }

  return messagesResponseSchema.parse(await response.json());
}

export async function sendMessage(
  discrepancyId: string,
  content: string
): Promise<void> {
  const url = buildApiUrl(`/v1/discrepancies/${discrepancyId}/messages`);
  const response = await authFetch(url, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });

  if (!response.ok) {
    throw new CreateError('message', response.statusText);
  }
}
